import {
  faArrowLeft,
  faArrowRight,
  faArchive,
  faBan,
  faBook,
  faBuilding,
  faCheck,
  faChevronDown,
  faCircle,
  faClock,
  faCommentDots,
  faDesktop,
  faEllipsisH,
  faEllipsisV,
  faEnvelope,
  faFilter,
  faGlobeAmericas,
  faHeadset,
  faInbox,
  faLink,
  faMapMarkerAlt,
  faMicrophone,
  faPalette,
  faPaperPlane,
  faPaperclip,
  faPhone,
  faPlus,
  faPlug,
  faSearch,
  faSignInAlt,
  faSmile,
  faTrashAlt,
  faUser,
  faVideo,
} from "@fortawesome/free-solid-svg-icons";
import {
  faFacebook,
  faInstagram,
  faLinkedin,
  faTiktok,
  faWhatsapp,
  faYoutube,
} from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import type { EmojiData } from "emoji-mart";
import { Picker } from "emoji-mart";
import "emoji-mart/css/emoji-mart.css";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button } from "reactstrap";
import { toast } from "react-toastify";
import SupportChatLivePreview from "../component/support/SupportChatLivePreview";
import Content from "../layout/Content";
import Layout from "../layout/Layout";
import "../styles/WembiiAdminSupport.scss";
import { SupportConversation, SupportMessage, SupportUserActivity, SupportVisitor } from "../../cores/wembiiSupport";
import { useRouterCore } from "../../cores/router";
import {
  defaultSupportAppearance,
  useWembiiAdminSupportScreen,
} from "./hooks/useWembiiAdminSupportScreen";

type SupportTab = "visitors" | "conversations" | "appearance";
type ConversationListFilter = "active" | "archived" | "all";

const CONVERSATION_FILTER_OPTIONS: Array<{
  id: ConversationListFilter;
  label: string;
  hint: string;
}> = [
  {
    id: "active",
    label: "Activas",
    hint: "Pendientes y resueltas",
  },
  {
    id: "archived",
    label: "Archivadas",
    hint: "Ocultas del seguimiento diario",
  },
  {
    id: "all",
    label: "Todas",
    hint: "Incluye archivadas",
  },
];

function conversationStatusLabel(status?: SupportConversation["status"]) {
  if (status === "archived") return "Archivada";
  if (status === "resolved") return "Resuelta";
  return "Pendiente";
}

interface SupportChatAppearance {
  widgetTitle: string;
  welcomeTitle: string;
  welcomeMessage: string;
  inputPlaceholder: string;
  ctaLabel: string;
  onlineLabel: string;
  headerColor: string;
  bubbleColor: string;
  widgetBg: string;
  textColor: string;
  accentColor: string;
  position: "left" | "right";
  avatarType: "logo" | "agent";
  showAgentName: boolean;
  showTimestamp: boolean;
  roundedStyle: "soft" | "rounded" | "square";
  showQuickButtons: boolean;
  showCommercialQuickButton: boolean;
  showTechnicalQuickButton: boolean;
  quickButtonCommercial: string;
  quickButtonTechnical: string;
}

const defaultAppearance = defaultSupportAppearance;

const TILE_SIZE = 256;
const SOUTH_AMERICA_CENTER = { lat: -20.5, lng: -62.5 };
const MIN_MAP_ZOOM = 3;
const MAX_MAP_ZOOM = 16;
const VISITOR_FOCUS_ZOOM = 12;

function getVisitorLocationKey(visitor: SupportVisitor) {
  return `${visitor.city}, ${visitor.country}`;
}

function getVisitorMapKey(visitor: SupportVisitor) {
  if (
    visitor.lat != null &&
    visitor.lng != null &&
    !Number.isNaN(visitor.lat) &&
    !Number.isNaN(visitor.lng)
  ) {
    return `${Number(visitor.lat).toFixed(3)},${Number(visitor.lng).toFixed(3)}`;
  }
  return getVisitorLocationKey(visitor);
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function formatLocationLabel(
  city?: string,
  country?: string,
  session?: { lat?: number | null; lng?: number | null } | null
) {
  const normalizedCity = city?.trim();
  const normalizedCountry = country?.trim();
  if (normalizedCity && normalizedCountry) return `${normalizedCity}, ${normalizedCountry}`;
  if (normalizedCity) return normalizedCity;
  if (normalizedCountry) return normalizedCountry;
  if (session?.lat != null && session?.lng != null) {
    return `~${Number(session.lat).toFixed(2)}, ${Number(session.lng).toFixed(2)}`;
  }
  return "Ubicación no disponible";
}

function formatIpLabel(ip?: string) {
  if (!ip) return "IP no disponible";
  if (ip === "::1" || ip === "127.0.0.1") return "Localhost (desarrollo)";
  return ip;
}

function formatLastSeenLabel(lastSeenAt?: string | null, isOnline?: boolean) {
  if (isOnline) return "En línea ahora";
  if (!lastSeenAt) return "Sin actividad reciente";
  const date = new Date(lastSeenAt);
  if (Number.isNaN(date.getTime())) return "Sin actividad reciente";

  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    date.getFullYear() === yesterday.getFullYear() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getDate() === yesterday.getDate();

  const time = new Intl.DateTimeFormat("es", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);

  if (sameDay) return `Hoy ${time}`;
  if (isYesterday) return `Ayer ${time}`;

  const day = new Intl.DateTimeFormat("es", {
    day: "numeric",
    month: "short",
    year: date.getFullYear() === now.getFullYear() ? undefined : "numeric",
  }).format(date);

  return `${day} ${time}`;
}

function formatActivityTimestamp(value?: string | null) {
  if (!value) return "Sin registro";
  return formatLastSeenLabel(value, false);
}

function formatAppActivityStatus(activity?: SupportUserActivity | null) {
  if (activity?.isActiveInApp) return "En línea en Wembii ahora";
  if (activity?.lastActiveAt) return "Inactivo en Wembii";
  return "Sin actividad registrada";
}

function formatSupportConnectionLabel(
  lastSeenAt?: string | null,
  isOnline?: boolean
) {
  if (isOnline) return "Conexión de soporte activa";
  if (!lastSeenAt) return "Sin conexión de soporte reciente";
  return `Última conexión de soporte: ${formatActivityTimestamp(lastSeenAt)}`;
}

function normalizeWhatsAppPhone(phone?: string) {
  const digits = String(phone || "").replace(/\D/g, "");
  return digits.length >= 8 ? digits : "";
}

function getWhatsAppUrl(phone?: string) {
  const digits = normalizeWhatsAppPhone(phone);
  return digits ? `https://wa.me/${digits}` : "";
}

function getIntegrationIcon(provider?: string) {
  switch (String(provider || "").toLowerCase()) {
    case "facebook":
      return faFacebook;
    case "instagram":
      return faInstagram;
    case "linkedin":
      return faLinkedin;
    case "tiktok":
      return faTiktok;
    case "youtube":
      return faYoutube;
    case "whatsapp":
      return faWhatsapp;
    default:
      return faPlug;
  }
}

function latLngToWorldPixel(lat: number, lng: number, zoom: number) {
  const sinLat = Math.sin((clamp(lat, -85.05112878, 85.05112878) * Math.PI) / 180);
  const scale = TILE_SIZE * Math.pow(2, zoom);
  return {
    x: ((lng + 180) / 360) * scale,
    y:
      (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) *
      scale,
  };
}

function worldPixelToLatLng(x: number, y: number, zoom: number) {
  const scale = TILE_SIZE * Math.pow(2, zoom);
  const lng = (x / scale) * 360 - 180;
  const latRad = Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / scale)));
  const lat = (latRad * 180) / Math.PI;
  return { lat, lng };
}

function mapPointToViewport(
  lat: number,
  lng: number,
  zoom: number,
  size: { width: number; height: number },
  centerLatLng: { lat: number; lng: number }
) {
  const center = latLngToWorldPixel(centerLatLng.lat, centerLatLng.lng, zoom);
  const point = latLngToWorldPixel(lat, lng, zoom);
  return {
    left: point.x - center.x + size.width / 2,
    top: point.y - center.y + size.height / 2,
  };
}


function getSupportInitials(name?: string) {
  if (!name?.trim()) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] ?? ""}${parts[parts.length - 1][0] ?? ""}`.toUpperCase();
}

const countryNameToCode: Record<string, string> = {
  Argentina: "AR",
  Chile: "CL",
  Uruguay: "UY",
  Brasil: "BR",
  Brazil: "BR",
  Colombia: "CO",
  Perú: "PE",
  Peru: "PE",
  México: "MX",
  Mexico: "MX",
  España: "ES",
  Spain: "ES",
};

function normalizeCountryCode(code?: string, country?: string) {
  const upper = String(code || "").trim().toUpperCase();
  if (upper.length === 2) return upper;
  return countryNameToCode[country || ""] || "";
}

function getCountryFlagUrl(countryCode?: string, country?: string) {
  const normalized = normalizeCountryCode(countryCode, country).toLowerCase();
  if (!normalized) return null;
  return `https://flagcdn.com/w40/${normalized}.png`;
}

type AvatarPresence = "online" | "idle" | "offline";

function resolveAvatarPresence(
  presence?: AvatarPresence,
  status?: SupportVisitor["status"]
): AvatarPresence | undefined {
  if (presence) return presence;
  if (status === "online") return "online";
  if (status === "idle") return "idle";
  return undefined;
}

function getPresenceTitle(presence: AvatarPresence) {
  if (presence === "online") return "En línea";
  if (presence === "idle") return "Inactivo";
  return "Desconectado";
}

function getConversationPresence(
  conversation: SupportConversation,
  visitors: SupportVisitor[]
): AvatarPresence {
  const liveVisitor = visitors.find(
    (visitor) =>
      visitor.conversationId === conversation.id ||
      visitor.id === conversation.visitorId ||
      visitor.userId === conversation.visitorId
  );
  if (liveVisitor) {
    return liveVisitor.status === "online" ? "online" : "idle";
  }
  return conversation.isOnline || conversation.session?.isOnline
    ? "online"
    : "offline";
}

function groupMessagesByDate(messages: SupportMessage[]) {
  const groups: Array<{ date: string; messages: SupportMessage[] }> = [];
  messages.forEach((message) => {
    const date = message.timestamp?.date || "Hoy";
    const last = groups[groups.length - 1];
    if (last?.date === date) {
      last.messages.push(message);
    } else {
      groups.push({ date, messages: [message] });
    }
  });
  return groups;
}

function SupportAvatar(props: {
  className: string;
  color: string;
  name?: string;
  avatarUrl?: string | null;
  flag?: string;
  countryCode?: string;
  country?: string;
  status?: SupportVisitor["status"];
  presence?: AvatarPresence;
  showPresence?: boolean;
}) {
  const {
    className,
    color,
    name,
    avatarUrl,
    flag,
    countryCode,
    country,
    status,
    presence,
    showPresence = true,
  } = props;
  const [imageFailed, setImageFailed] = useState(false);
  const [flagImageFailed, setFlagImageFailed] = useState(false);
  const normalizedAvatarUrl = avatarUrl?.trim() || undefined;
  const resolvedPresence = resolveAvatarPresence(presence, status);
  const normalizedCountryCode = normalizeCountryCode(countryCode, country);
  const flagUrl = getCountryFlagUrl(countryCode, country);
  const showFlagBadge = Boolean(flagUrl || normalizedCountryCode || flag);

  useEffect(() => {
    setImageFailed(false);
  }, [normalizedAvatarUrl]);

  useEffect(() => {
    setFlagImageFailed(false);
  }, [flagUrl]);

  const showImage = Boolean(normalizedAvatarUrl && !imageFailed);
  const initials = getSupportInitials(name);

  return (
    <span className={className} style={{ background: showImage ? "#fff" : color }}>
      {showImage ? (
        <img
          src={normalizedAvatarUrl}
          alt=""
          loading="lazy"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span className="support-avatar__initials" aria-hidden="true">
          {initials}
        </span>
      )}
      {showPresence && resolvedPresence && (
        <span
          className="support-avatar__presence"
          data-presence={resolvedPresence}
          title={getPresenceTitle(resolvedPresence)}
        />
      )}
      {showFlagBadge ? (
        <span className="support-avatar__flag" aria-hidden="true">
          {flagUrl && !flagImageFailed ? (
            <img
              src={flagUrl}
              alt=""
              loading="lazy"
              onError={() => setFlagImageFailed(true)}
            />
          ) : normalizedCountryCode ? (
            <span className="support-avatar__flag-code">{normalizedCountryCode}</span>
          ) : (
            flag
          )}
        </span>
      ) : null}
    </span>
  );
}

export default function WembiiAdminSupport() {
  const [activeTab, setActiveTab] = useState<SupportTab>("visitors");
  const { goToUserProfile } = useRouterCore();
  const {
    appearance,
    conversations,
    selectedConversation,
    messages,
    messagesLoading,
    supportVisitors,
    replyDraft,
    setReplyDraft,
    sendAgentReply,
    setAppearanceField,
    resetAppearance,
    uploadAvatar,
    openVisitorConversation: openVisitorFromHook,
    selectConversation,
    setConversationStatus,
    formatLocalTime,
  } = useWembiiAdminSupportScreen(activeTab);

  const mapRef = useRef<HTMLElement>(null);
  const mapCenterRef = useRef(SOUTH_AMERICA_CENTER);
  const mapZoomRef = useRef(4);
  const mapDragRef = useRef<{
    startX: number;
    startY: number;
    center: { lat: number; lng: number };
    zoom: number;
  } | null>(null);
  const filterRef = useRef<HTMLDivElement>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [listFilter, setListFilter] = useState<ConversationListFilter>("active");
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [topMenuOpen, setTopMenuOpen] = useState(false);
  const [knowledgeOpen, setKnowledgeOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const emojiPanelRef = useRef<HTMLDivElement>(null);
  const emojiToggleRef = useRef<HTMLButtonElement>(null);
  const replyInputRef = useRef<HTMLTextAreaElement>(null);
  const threadEndRef = useRef<HTMLDivElement>(null);
  const [mapZoom, setMapZoom] = useState(4);
  const [mapCenter, setMapCenter] = useState(SOUTH_AMERICA_CENTER);
  const [mapSize, setMapSize] = useState({ width: 900, height: 620 });
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [selectedVisitorId, setSelectedVisitorId] = useState<string | null>(null);
  const [isMapDragging, setIsMapDragging] = useState(false);
  const [mobileInboxView, setMobileInboxView] = useState<"list" | "chat">("list");
  const [conversationSearch, setConversationSearch] = useState("");
  const [showProfilePanel, setShowProfilePanel] = useState(false);

  useEffect(() => {
    mapCenterRef.current = mapCenter;
  }, [mapCenter]);

  useEffect(() => {
    mapZoomRef.current = mapZoom;
  }, [mapZoom]);

  useEffect(() => {
    if (!emojiOpen) return;
    const closeIfOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (
        emojiPanelRef.current?.contains(target) ||
        emojiToggleRef.current?.contains(target)
      ) {
        return;
      }
      setEmojiOpen(false);
    };
    document.addEventListener("mousedown", closeIfOutside);
    document.addEventListener("touchstart", closeIfOutside);
    return () => {
      document.removeEventListener("mousedown", closeIfOutside);
      document.removeEventListener("touchstart", closeIfOutside);
    };
  }, [emojiOpen]);

  useEffect(() => {
    if (!filterOpen) return;
    const closeIfOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (filterRef.current?.contains(target)) return;
      setFilterOpen(false);
    };
    document.addEventListener("mousedown", closeIfOutside);
    document.addEventListener("touchstart", closeIfOutside);
    return () => {
      document.removeEventListener("mousedown", closeIfOutside);
      document.removeEventListener("touchstart", closeIfOutside);
    };
  }, [filterOpen]);

  const onEmojiSelect = useCallback(
    (emoji: EmojiData) => {
      if ("native" in emoji && emoji.native) {
        setReplyDraft((prev) => prev + emoji.native);
      }
      replyInputRef.current?.focus();
    },
    [setReplyDraft]
  );

  const zoomMapAtPoint = useCallback(
    (clientX: number, clientY: number, delta: number) => {
      const mapEl = mapRef.current;
      if (!mapEl) return;

      const rect = mapEl.getBoundingClientRect();
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      const currentZoom = mapZoomRef.current;
      const nextZoom = clamp(currentZoom + delta, MIN_MAP_ZOOM, MAX_MAP_ZOOM);
      if (nextZoom === currentZoom) return;

      const center = mapCenterRef.current;
      const centerPx = latLngToWorldPixel(center.lat, center.lng, currentZoom);
      const focusPx = {
        x: centerPx.x + (x - mapSize.width / 2),
        y: centerPx.y + (y - mapSize.height / 2),
      };
      const focusLatLng = worldPixelToLatLng(focusPx.x, focusPx.y, currentZoom);
      const focusPxNext = latLngToWorldPixel(
        focusLatLng.lat,
        focusLatLng.lng,
        nextZoom
      );
      const nextCenterPx = {
        x: focusPxNext.x - (x - mapSize.width / 2),
        y: focusPxNext.y - (y - mapSize.height / 2),
      };

      setMapZoom(nextZoom);
      setMapCenter(worldPixelToLatLng(nextCenterPx.x, nextCenterPx.y, nextZoom));
    },
    [mapSize.height, mapSize.width]
  );

  const handleMapMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.button !== 0) return;
      mapDragRef.current = {
        startX: event.clientX,
        startY: event.clientY,
        center: { ...mapCenterRef.current },
        zoom: mapZoomRef.current,
      };
      setIsMapDragging(true);
    },
    []
  );

  useEffect(() => {
    if (!isMapDragging) return;

    const handleMouseMove = (event: MouseEvent) => {
      const drag = mapDragRef.current;
      if (!drag) return;
      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      const centerPx = latLngToWorldPixel(drag.center.lat, drag.center.lng, drag.zoom);
      const nextCenter = worldPixelToLatLng(
        centerPx.x - dx,
        centerPx.y - dy,
        drag.zoom
      );
      setMapCenter(nextCenter);
    };

    const handleMouseUp = () => {
      mapDragRef.current = null;
      setIsMapDragging(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isMapDragging]);

  useEffect(() => {
    const mapEl = mapRef.current;
    if (!mapEl || activeTab !== "visitors") return;

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      const delta = event.deltaY > 0 ? -1 : 1;
      zoomMapAtPoint(event.clientX, event.clientY, delta);
    };

    mapEl.addEventListener("wheel", handleWheel, { passive: false });
    return () => mapEl.removeEventListener("wheel", handleWheel);
  }, [activeTab, zoomMapAtPoint]);

  const openVisitorConversation = async (visitor: SupportVisitor) => {
    const opened = await openVisitorFromHook(visitor);
    if (!opened) return;
    setActiveTab("conversations");
    setMobileInboxView("chat");
    setFilterOpen(false);
    setTopMenuOpen(false);
    setKnowledgeOpen(false);
  };

  const handleSelectConversation = useCallback(
    (conversationId: string) => {
      selectConversation(conversationId);
      setMobileInboxView("chat");
      setShowProfilePanel(false);
      setTopMenuOpen(false);
      setKnowledgeOpen(false);
    },
    [selectConversation]
  );

  const focusVisitorOnMap = useCallback((visitor: SupportVisitor) => {
    if (visitor.lat == null || visitor.lng == null) return;
    setSelectedVisitorId(visitor.id);
    setSelectedLocationId(getVisitorMapKey(visitor));
    setMapCenter({ lat: visitor.lat, lng: visitor.lng });
    setMapZoom(VISITOR_FOCUS_ZOOM);
  }, []);

  const focusLocationOnMap = useCallback(
    (location: { id: string; lat: number; lng: number }) => {
      setSelectedLocationId(location.id);
      setMapCenter({ lat: location.lat, lng: location.lng });
      setMapZoom((zoom) => clamp(Math.max(zoom, VISITOR_FOCUS_ZOOM - 1), MIN_MAP_ZOOM, MAX_MAP_ZOOM));
    },
    []
  );

  useEffect(() => {
    const el = mapRef.current;
    if (!el) return;
    const syncSize = () =>
      setMapSize({ width: el.clientWidth || 900, height: el.clientHeight || 620 });
    syncSize();
    const observer = new ResizeObserver(syncSize);
    observer.observe(el);
    return () => observer.disconnect();
  }, [activeTab]);

  useEffect(() => {
    document.body.classList.add("hide-wembii-support-floating-widget");
    return () =>
      document.body.classList.remove("hide-wembii-support-floating-widget");
  }, []);

  useEffect(() => {
    if (activeTab !== "conversations" || messagesLoading) return;
    threadEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [activeTab, messages, messagesLoading, selectedConversation?.id]);

  const onlineVisitors = supportVisitors.filter(
    (visitor) => visitor.status === "online"
  );

  const mapLocations = useMemo(() => {
    const mappableVisitors = supportVisitors.filter(
      (visitor) =>
        visitor.id !== "detected-browser-user" &&
        visitor.lat != null &&
        visitor.lng != null &&
        !Number.isNaN(visitor.lat) &&
        !Number.isNaN(visitor.lng)
    );

    const grouped = mappableVisitors.reduce<Record<string, SupportVisitor[]>>(
      (acc, visitor) => {
        const key = `${Number(visitor.lat).toFixed(3)},${Number(visitor.lng).toFixed(3)}`;
        acc[key] = [...(acc[key] ?? []), visitor];
        return acc;
      },
      {}
    );

    return Object.entries(grouped).map(([id, group]) => ({
      id,
      label: getVisitorLocationKey(group[0]),
      flag: group[0].flag,
      visitors: group,
      lat: group.reduce((sum, visitor) => sum + (visitor.lat ?? 0), 0) / group.length,
      lng: group.reduce((sum, visitor) => sum + (visitor.lng ?? 0), 0) / group.length,
    }));
  }, [supportVisitors]);

  const selectedLocation = selectedLocationId
    ? mapLocations.find((location) => location.id === selectedLocationId) ?? null
    : null;

  const selectedVisitor = selectedVisitorId
    ? supportVisitors.find((visitor) => visitor.id === selectedVisitorId) ?? null
    : null;

  const filteredConversations = useMemo(() => {
    const query = conversationSearch.trim().toLowerCase();
    return conversations.filter((conversation) => {
      const status = conversation.status || "pending";
      if (listFilter === "active" && status === "archived") return false;
      if (listFilter === "archived" && status !== "archived") return false;
      if (!query) return true;
      return (
        conversation.name.toLowerCase().includes(query) ||
        conversation.wembiiCompanyName.toLowerCase().includes(query) ||
        conversation.snippet.toLowerCase().includes(query) ||
        conversation.email.toLowerCase().includes(query)
      );
    });
  }, [conversationSearch, conversations, listFilter]);

  const handleConversationStatusChange = useCallback(
    async (status: SupportConversation["status"]) => {
      if (!selectedConversation?.id || statusUpdating) return;
      setStatusUpdating(true);
      setTopMenuOpen(false);
      try {
        await setConversationStatus(selectedConversation.id, status);
        if (status === "archived") {
          toast.success("Conversación archivada");
          if (listFilter === "active") {
            setListFilter("archived");
          }
        } else if (status === "pending") {
          toast.success(
            selectedConversation.status === "archived"
              ? "Conversación restaurada"
              : "Conversación marcada como pendiente"
          );
          if (listFilter === "archived") {
            setListFilter("active");
          }
        } else if (status === "resolved") {
          toast.success("Conversación marcada como resuelta");
        }
      } catch {
        toast.error("No se pudo actualizar el estado de la conversación");
      } finally {
        setStatusUpdating(false);
      }
    },
    [
      selectedConversation?.id,
      selectedConversation?.status,
      statusUpdating,
      setConversationStatus,
      listFilter,
    ]
  );

  const messageGroups = useMemo(() => groupMessagesByDate(messages), [messages]);

  const mapTiles = useMemo(() => {
    const center = latLngToWorldPixel(mapCenter.lat, mapCenter.lng, mapZoom);
    const worldTiles = Math.pow(2, mapZoom);
    const cols = Math.ceil(mapSize.width / TILE_SIZE) + 3;
    const rows = Math.ceil(mapSize.height / TILE_SIZE) + 3;
    const startX = Math.floor((center.x - mapSize.width / 2) / TILE_SIZE) - 1;
    const startY = Math.floor((center.y - mapSize.height / 2) / TILE_SIZE) - 1;
    const tiles: Array<{
      id: string;
      x: number;
      y: number;
      srcX: number;
      srcY: number;
      left: number;
      top: number;
    }> = [];

    for (let dx = 0; dx < cols; dx += 1) {
      for (let dy = 0; dy < rows; dy += 1) {
        const x = startX + dx;
        const y = clamp(startY + dy, 0, worldTiles - 1);
        const srcX = ((x % worldTiles) + worldTiles) % worldTiles;
        tiles.push({
          id: `${mapZoom}-${x}-${y}`,
          x,
          y,
          srcX,
          srcY: y,
          left: x * TILE_SIZE - center.x + mapSize.width / 2,
          top: y * TILE_SIZE - center.y + mapSize.height / 2,
        });
      }
    }
    return tiles;
  }, [mapCenter.lat, mapCenter.lng, mapSize.height, mapSize.width, mapZoom]);

  return (
    <Layout>
      <Content className="wembii-admin-support-content">
        <div
          className={cn("wembii-admin-support", {
            "wembii-admin-support--appearance-active": activeTab === "appearance",
            "wembii-admin-support--mobile-chat":
              activeTab === "conversations" &&
              mobileInboxView === "chat" &&
              !!selectedConversation,
          })}
        >
          <div className="wembii-admin-support__hero">
            <div>
              <div className="wembii-admin-support__badge">
                <FontAwesomeIcon icon={faHeadset} />
                Soporte Wembii
              </div>
              <h2>Centro de soporte gestionado por Wembii</h2>
            </div>
            <div className="wembii-admin-support__tabs" role="tablist">
              <button
                type="button"
                className={cn({ active: activeTab === "visitors" })}
                onClick={() => setActiveTab("visitors")}
              >
                <FontAwesomeIcon icon={faGlobeAmericas} />
                Visitantes
              </button>
              <button
                type="button"
                className={cn({ active: activeTab === "conversations" })}
                onClick={() => setActiveTab("conversations")}
              >
                <FontAwesomeIcon icon={faCommentDots} />
                Conversaciones
              </button>
              <button
                type="button"
                className={cn({ active: activeTab === "appearance" })}
                onClick={() => setActiveTab("appearance")}
              >
                <FontAwesomeIcon icon={faPalette} />
                Apariencia chat
              </button>
            </div>
          </div>

          {activeTab === "visitors" ? (
            <div className="support-live">
              <aside className="support-live__sidebar">
                <div className="support-live__head">
                  <span>Visitantes</span>
                  <Badge color="warning" pill>
                    En vivo
                  </Badge>
                </div>
                <div className="support-live__list">
                  {supportVisitors.map((visitor) => (
                    <div
                      key={visitor.id}
                      className={cn("support-live-user", {
                        "support-live-user--active": selectedVisitorId === visitor.id,
                      })}
                    >
                      <button
                        type="button"
                        className="support-live-user__main"
                        onClick={() => focusVisitorOnMap(visitor)}
                      >
                        <SupportAvatar
                          className="support-avatar support-avatar--lg"
                          color={visitor.color}
                          name={visitor.name}
                          avatarUrl={visitor.avatarUrl}
                          flag={visitor.flag}
                          countryCode={visitor.countryCode}
                          country={visitor.country}
                          status={
                            visitor.id === "detected-browser-user"
                              ? undefined
                              : visitor.status
                          }
                          showPresence={visitor.id !== "detected-browser-user"}
                        />
                        <span>
                          <strong>{visitor.name}</strong>
                          <small>{visitor.wembiiCompanyName}</small>
                        </span>
                      </button>
                      <button
                        type="button"
                        className="support-live-user__chat"
                        aria-label={`Ir al chat con ${visitor.name}`}
                        title="Ir al chat"
                        onClick={() => openVisitorConversation(visitor)}
                      >
                        <FontAwesomeIcon icon={faCommentDots} />
                      </button>
                    </div>
                  ))}
                </div>
                <label className="support-live-search">
                  <FontAwesomeIcon icon={faSearch} />
                  <input placeholder="Filtrar por nombre, ciudad..." />
                </label>
              </aside>
              <section
                className={cn("support-map", {
                  "support-map--dragging": isMapDragging,
                })}
                ref={mapRef}
              >
                <div
                  className="support-map__surface"
                  onMouseDown={handleMapMouseDown}
                  role="presentation"
                >
                  <div className="support-map__tile-layer" aria-hidden>
                    {mapTiles.map((tile) => (
                      <img
                        key={tile.id}
                        className="support-map__tile"
                        alt=""
                        draggable={false}
                        src={`https://tile.openstreetmap.org/${mapZoom}/${tile.srcX}/${tile.srcY}.png`}
                        style={{
                          left: tile.left,
                          top: tile.top,
                        }}
                      />
                    ))}
                  </div>
                </div>
                <div className="support-map-card support-map__overlay">
                  <strong>{supportVisitors.length}</strong> usuarios en línea
                  <br />
                  <strong className="text-success">{onlineVisitors.length}</strong>{" "}
                  usuarios activos ahora
                  <small>Zoom {mapZoom} · OpenStreetMap + MagicMap</small>
                </div>
                {selectedLocation && (
                <div className="support-map-location-panel support-map__overlay">
                  <div className="support-map-location-panel__head">
                    <span>{selectedLocation.flag}</span>
                    <strong>{selectedLocation.label}</strong>
                    <button
                      type="button"
                      aria-label="Cerrar"
                      onClick={() => setSelectedLocationId("")}
                    >
                      ×
                    </button>
                    <small>
                      {selectedLocation.visitors.length === 1
                        ? "1 visitante en esta ubicación"
                        : `${selectedLocation.visitors.length} visitantes en esta ubicación`}
                    </small>
                  </div>
                  <div className="support-map-location-panel__body">
                    {selectedLocation.visitors.map((visitor) => (
                      <div
                        key={visitor.id}
                        className={cn("support-map-location-user-row", {
                          "support-map-location-user-row--active":
                            selectedVisitorId === visitor.id,
                        })}
                      >
                        <button
                          type="button"
                          className="support-map-location-user"
                          onClick={() => focusVisitorOnMap(visitor)}
                        >
                          <SupportAvatar
                            className="support-avatar support-avatar--lg"
                            color={visitor.color}
                            name={visitor.name}
                            avatarUrl={visitor.avatarUrl}
                            flag={visitor.flag}
                            countryCode={visitor.countryCode}
                            country={visitor.country}
                            status={
                              visitor.id === "detected-browser-user"
                                ? undefined
                                : visitor.status
                            }
                            showPresence={visitor.id !== "detected-browser-user"}
                          />
                          <span>
                            <strong>{visitor.name}</strong>
                            <small>{visitor.wembiiCompanyName}</small>
                          </span>
                        </button>
                        <button
                          type="button"
                          className="support-live-user__chat"
                          aria-label={`Ir al chat con ${visitor.name}`}
                          title="Ir al chat"
                          onClick={() => openVisitorConversation(visitor)}
                        >
                          <FontAwesomeIcon icon={faCommentDots} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
                )}
                {mapLocations.map((location) => {
                  const position = mapPointToViewport(
                    location.lat,
                    location.lng,
                    mapZoom,
                    mapSize,
                    mapCenter
                  );
                  const isSelected = selectedLocation?.id === location.id;
                  return (
                    <button
                      key={location.id}
                      type="button"
                      className={cn("support-map-location-marker support-map__overlay", {
                        "support-map-location-marker--selected": isSelected,
                        "support-map-location-marker--focused":
                          !!selectedVisitor &&
                          location.visitors.some((visitor) => visitor.id === selectedVisitor.id),
                        "support-map-location-marker--detected":
                          location.id.includes("Browser"),
                      })}
                      style={{
                        left: position.left,
                        top: position.top,
                      }}
                      title={`${location.label} · ${location.visitors.length} usuarios`}
                      onClick={() => focusLocationOnMap(location)}
                    >
                      <span className="support-map-marker support-map-marker--cluster">
                        {location.visitors.length}
                      </span>
                      {isSelected && (
                        <span className="support-map-tooltip">
                          <strong>
                            {location.flag} {location.label}
                          </strong>
                          <span>
                            {location.visitors.length === 1
                              ? "1 visitante en línea"
                              : `${location.visitors.length} visitantes en línea`}
                          </span>
                        </span>
                      )}
                    </button>
                  );
                })}
                <div className="support-map-zoom support-map__overlay">
                  <button
                    type="button"
                    onClick={() =>
                      setMapZoom((zoom) => clamp(zoom + 1, MIN_MAP_ZOOM, MAX_MAP_ZOOM))
                    }
                  >
                    +
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setMapZoom((zoom) => clamp(zoom - 1, MIN_MAP_ZOOM, MAX_MAP_ZOOM))
                    }
                  >
                    −
                  </button>
                </div>
                <div className="support-map-attribution">
                  © OpenStreetMap · detección vía navegador
                </div>
              </section>
            </div>
          ) : activeTab === "conversations" ? (
            <div
              className={cn("support-inbox", {
                "support-inbox--chat-open": mobileInboxView === "chat" && !!selectedConversation,
                "support-inbox--profile-open": showProfilePanel,
              })}
            >
              <aside
                className={cn("support-inbox__list", {
                  "support-inbox__list--hidden-mobile":
                    mobileInboxView === "chat" && !!selectedConversation,
                })}
              >
                <div className="support-inbox-toolbar">
                  <strong className="support-inbox-toolbar__title">
                    {listFilter === "archived" ? "Archivadas" : "Chats"}
                  </strong>
                  <div className="support-select-wrap" ref={filterRef}>
                    <button
                      type="button"
                      className={cn("support-toolbar-btn", {
                        active: filterOpen || listFilter !== "active",
                      })}
                      aria-label="Filtros"
                      aria-expanded={filterOpen}
                      onClick={() => setFilterOpen((open) => !open)}
                    >
                      <FontAwesomeIcon icon={faFilter} />
                    </button>
                    {filterOpen ? (
                      <div className="support-dropdown support-dropdown--filters">
                        {CONVERSATION_FILTER_OPTIONS.map((option) => (
                          <button
                            key={option.id}
                            type="button"
                            className={cn({ active: listFilter === option.id })}
                            onClick={() => {
                              setListFilter(option.id);
                              setFilterOpen(false);
                            }}
                          >
                            <FontAwesomeIcon
                              icon={
                                option.id === "archived"
                                  ? faArchive
                                  : option.id === "all"
                                  ? faInbox
                                  : faCommentDots
                              }
                            />
                            <span>
                              <strong>{option.label}</strong>
                              <small>{option.hint}</small>
                            </span>
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
                <label className="support-inbox-search">
                  <FontAwesomeIcon icon={faSearch} />
                  <input
                    placeholder="Buscar conversación..."
                    value={conversationSearch}
                    onChange={(e) => setConversationSearch(e.target.value)}
                  />
                </label>
                <div className="support-conversation-list">
                  {filteredConversations.length === 0 ? (
                    <div className="support-conversation-list__empty">
                      {listFilter === "archived"
                        ? "No hay conversaciones archivadas."
                        : "No hay conversaciones que coincidan."}
                    </div>
                  ) : (
                    filteredConversations.map((conversation) => (
                      <button
                        key={conversation.id}
                        type="button"
                        className={cn("support-conversation-item", {
                          selected: conversation.id === selectedConversation?.id,
                          unread: conversation.unread,
                          archived: conversation.status === "archived",
                        })}
                        onClick={() => handleSelectConversation(conversation.id)}
                      >
                        <SupportAvatar
                          className="support-avatar support-avatar--lg"
                          color={conversation.color}
                          name={conversation.name}
                          avatarUrl={conversation.avatarUrl}
                          flag={conversation.flag}
                          countryCode={conversation.countryCode}
                          country={conversation.country}
                          presence={getConversationPresence(conversation, supportVisitors)}
                        />
                        <span className="support-conversation-item__body">
                          <span className="support-conversation-item__top">
                            <strong>{conversation.name}</strong>
                            <span>{conversation.date}</span>
                          </span>
                          <span className="support-conversation-item__bottom">
                            <small>{conversation.wembiiCompanyName}</small>
                            <small>
                              {conversation.status === "archived"
                                ? "Archivada"
                                : conversation.snippet}
                            </small>
                          </span>
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </aside>

              <main
                className={cn("support-chat-admin", {
                  "support-chat-admin--hidden-mobile": mobileInboxView === "list",
                })}
              >
                {!selectedConversation ? (
                  <div className="support-chat-admin__empty">
                    <FontAwesomeIcon icon={faCommentDots} />
                    <h3>Selecciona una conversación</h3>
                    <p>Elige un chat de la lista para responder como agente de soporte.</p>
                  </div>
                ) : (
                  <>
                    <header className="support-chat-header">
                      <button
                        type="button"
                        className="support-chat-header__back"
                        aria-label="Volver al listado"
                        onClick={() => setMobileInboxView("list")}
                      >
                        <FontAwesomeIcon icon={faArrowLeft} />
                      </button>
                      <button
                        type="button"
                        className="support-chat-header__profile"
                        onClick={() => setShowProfilePanel((open) => !open)}
                      >
                        <SupportAvatar
                          className="support-avatar support-avatar--md"
                          color={selectedConversation.color}
                          name={selectedConversation.name}
                          avatarUrl={selectedConversation.avatarUrl}
                          flag={selectedConversation.flag}
                          countryCode={selectedConversation.countryCode}
                          country={selectedConversation.country}
                          presence={getConversationPresence(selectedConversation, supportVisitors)}
                        />
                        <span>
                          <strong>{selectedConversation.name}</strong>
                          <small>{selectedConversation.wembiiCompanyName}</small>
                        </span>
                      </button>
                      <div className="support-chat-header__actions">
                        <button
                          type="button"
                          className={cn(
                            "support-status-btn support-status-btn--compact",
                            {
                              "support-status-btn--archived":
                                selectedConversation.status === "archived",
                              "support-status-btn--resolved":
                                selectedConversation.status === "resolved",
                            }
                          )}
                          disabled={statusUpdating}
                          onClick={() =>
                            void handleConversationStatusChange(
                              selectedConversation.status === "archived"
                                ? "pending"
                                : selectedConversation.status === "resolved"
                                ? "pending"
                                : "resolved"
                            )
                          }
                          title="Cambiar estado"
                        >
                          {conversationStatusLabel(selectedConversation.status)}
                        </button>
                        <div className="support-select-wrap">
                          <button
                            type="button"
                            className={cn("support-more-btn", { active: topMenuOpen })}
                            aria-label="Más opciones"
                            onClick={() => setTopMenuOpen((open) => !open)}
                          >
                            <FontAwesomeIcon icon={faEllipsisV} />
                          </button>
                          {topMenuOpen && (
                            <div className="support-dropdown support-dropdown--conversation">
                              {selectedConversation.status === "archived" ? (
                                <button
                                  type="button"
                                  disabled={statusUpdating}
                                  onClick={() =>
                                    void handleConversationStatusChange("pending")
                                  }
                                >
                                  <FontAwesomeIcon icon={faInbox} /> Restaurar conversación
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  disabled={statusUpdating}
                                  onClick={() =>
                                    void handleConversationStatusChange("archived")
                                  }
                                >
                                  <FontAwesomeIcon icon={faArchive} /> Archivar conversación
                                </button>
                              )}
                              {selectedConversation.status !== "resolved" ? (
                                <button
                                  type="button"
                                  disabled={statusUpdating}
                                  onClick={() =>
                                    void handleConversationStatusChange("resolved")
                                  }
                                >
                                  <FontAwesomeIcon icon={faCheck} /> Marcar como resuelta
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  disabled={statusUpdating}
                                  onClick={() =>
                                    void handleConversationStatusChange("pending")
                                  }
                                >
                                  <FontAwesomeIcon icon={faCommentDots} /> Marcar como pendiente
                                </button>
                              )}
                              <hr />
                              <button type="button" disabled>
                                <FontAwesomeIcon icon={faLink} /> Copiar enlace
                              </button>
                              <button type="button" disabled>
                                <FontAwesomeIcon icon={faBook} /> Transcripción
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </header>

                    <div className="support-chat-thread">
                      {messagesLoading ? (
                        <div className="support-chat-thread__loading">
                          <span className="support-chat-thread__spinner" aria-hidden />
                          Cargando conversación...
                        </div>
                      ) : (
                        <>
                          {messageGroups.map((group) => (
                            <React.Fragment key={group.date}>
                              <div className="support-date-pill">{group.date}</div>
                              {group.messages.map((message) => {
                                const isUser = message.senderType === "user";
                                return (
                                  <div
                                    key={message.id}
                                    className={cn("support-message-row", {
                                      "support-message-row--user": isUser,
                                      "support-message-row--agent": !isUser,
                                    })}
                                  >
                                    <div className="support-message-bubble">
                                      {!isUser && message.senderName && (
                                        <span className="support-message-bubble__sender">
                                          {message.senderName}
                                        </span>
                                      )}
                                      <p>{message.content}</p>
                                      {message.timestamp?.time && (
                                        <span className="support-message-bubble__time">
                                          {message.timestamp.time}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </React.Fragment>
                          ))}
                          <div ref={threadEndRef} aria-hidden />
                        </>
                      )}
                    </div>

                    <footer className="support-composer-bar">
                      {knowledgeOpen && (
                        <div className="support-knowledge-popover support-knowledge-popover--compact">
                          <div className="support-knowledge-popover__head">
                            <strong>Base de conocimiento</strong>
                            <button type="button" onClick={() => setKnowledgeOpen(false)}>
                              ×
                            </button>
                          </div>
                          <div className="support-knowledge-articles">
                            {[
                              "Cómo crear una cuenta en Mercado Libre",
                              "Cómo cambiar una cuenta de Instagram a Empresa ?",
                              "Cómo activar notificaciones de Chat en la App ?",
                            ].map((article) => (
                              <button key={article} type="button">
                                {article}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="support-composer-bar__tools">
                        <button
                          type="button"
                          className={cn({ active: knowledgeOpen })}
                          onClick={() => setKnowledgeOpen((open) => !open)}
                        >
                          <FontAwesomeIcon icon={faBook} />
                          <span>Artículos</span>
                        </button>
                      </div>
                      <div className="support-composer-bar__input-row">
                        <button
                          ref={emojiToggleRef}
                          type="button"
                          className={cn("support-composer-bar__icon", { active: emojiOpen })}
                          aria-label="Emojis"
                          onClick={() => setEmojiOpen((open) => !open)}
                        >
                          <FontAwesomeIcon icon={faSmile} />
                        </button>
                        <textarea
                          ref={replyInputRef}
                          className="support-composer-bar__input"
                          rows={1}
                          placeholder={`Escribe un mensaje para ${selectedConversation.name.split(" ")[0]}...`}
                          value={replyDraft}
                          onChange={(e) => setReplyDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              sendAgentReply();
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="support-composer-bar__send"
                          aria-label="Enviar"
                          onClick={sendAgentReply}
                          disabled={!replyDraft.trim()}
                        >
                          <FontAwesomeIcon icon={faPaperPlane} />
                        </button>
                      </div>
                      {emojiOpen && (
                        <div className="support-composer__emoji-panel" ref={emojiPanelRef}>
                          <Picker
                            native
                            color="#25d366"
                            sheetSize={32}
                            showPreview
                            showSkinTones
                            exclude={["recent"]}
                            onSelect={onEmojiSelect}
                            style={{
                              border: "none",
                              boxShadow: "none",
                              width: "100%",
                              height: 260,
                              maxHeight: 260,
                            }}
                          />
                        </div>
                      )}
                    </footer>
                  </>
                )}
              </main>

              <aside
                className={cn("support-profile", {
                  "support-profile--drawer-open": showProfilePanel,
                })}
              >
                {selectedConversation ? (
                  <>
                    <div className="support-profile__drawer-head">
                      <strong>Info del contacto</strong>
                      <button type="button" onClick={() => setShowProfilePanel(false)}>
                        ×
                      </button>
                    </div>
                <div className="support-profile__head">
                  <SupportAvatar
                    className="support-avatar support-avatar--profile"
                    color={selectedConversation.color}
                    name={selectedConversation.name}
                    avatarUrl={selectedConversation.avatarUrl}
                    flag={selectedConversation.flag}
                    countryCode={selectedConversation.countryCode}
                    country={selectedConversation.country}
                    presence={getConversationPresence(selectedConversation, supportVisitors)}
                  />
                  <div>
                    <strong>{selectedConversation.name}</strong>
                    <a href={`mailto:${selectedConversation.email}`}>{selectedConversation.email}</a>
                    {selectedConversation.phoneNumber ? (
                      getWhatsAppUrl(selectedConversation.phoneNumber) ? (
                        <a
                          className="support-profile__whatsapp"
                          href={getWhatsAppUrl(selectedConversation.phoneNumber)}
                          target="_blank"
                          rel="noreferrer"
                          title="Abrir WhatsApp"
                        >
                          <FontAwesomeIcon icon={faWhatsapp} />{" "}
                          {selectedConversation.phoneNumber}
                        </a>
                      ) : (
                        <a href={`tel:${selectedConversation.phoneNumber}`}>
                          <FontAwesomeIcon icon={faPhone} />{" "}
                          {selectedConversation.phoneNumber}
                        </a>
                      )
                    ) : (
                      <small>Sin teléfono registrado</small>
                    )}
                    <small>
                      {formatLocationLabel(
                        selectedConversation.city,
                        selectedConversation.country,
                        selectedConversation.session
                      )}{" "}
                      · {selectedConversation.wembiiCompanyName}
                    </small>
                  </div>
                </div>
                <Button
                  color="primary"
                  block
                  disabled={!selectedConversation.email}
                  onClick={() => goToUserProfile(selectedConversation.email)}
                >
                  <FontAwesomeIcon icon={faUser} /> Ver perfil de {selectedConversation.name.split(" ")[0]}
                </Button>
                <div className="support-profile-section support-profile-section--activity">
                  <h4>Actividad en Wembii</h4>
                  <div
                    className="support-profile-activity__status"
                    data-active={Boolean(selectedConversation.userActivity?.isActiveInApp)}
                  >
                    <span className="support-profile-activity__dot" aria-hidden />
                    <strong>{formatAppActivityStatus(selectedConversation.userActivity)}</strong>
                  </div>
                  <p>
                    <FontAwesomeIcon icon={faDesktop} />{" "}
                    {selectedConversation.userActivity?.lastActiveModule
                      ? `Módulo: ${selectedConversation.userActivity.lastActiveModule}`
                      : "Módulo: sin registro"}
                  </p>
                  <p>
                    <FontAwesomeIcon icon={faClock} />{" "}
                    {selectedConversation.userActivity?.lastActiveAt
                      ? `Última actividad: ${formatActivityTimestamp(
                          selectedConversation.userActivity.lastActiveAt
                        )}`
                      : "Última actividad: sin registro"}
                  </p>
                  <p>
                    <FontAwesomeIcon icon={faSignInAlt} />{" "}
                    {selectedConversation.userActivity?.lastLoginAt
                      ? `Último ingreso: ${formatActivityTimestamp(
                          selectedConversation.userActivity.lastLoginAt
                        )}`
                      : "Último ingreso: sin registro"}
                  </p>
                  <p className="support-profile-activity__hint">
                    Ideal para contacto en vivo si aparece en línea o con actividad de hace pocos minutos.
                  </p>
                </div>
                <div className="support-profile-section">
                  <h4>Conexión de soporte</h4>
                  <p>
                    <FontAwesomeIcon icon={faHeadset} />{" "}
                    {formatSupportConnectionLabel(
                      selectedConversation.session?.lastSeenAt ||
                        selectedConversation.lastSeenAt,
                      Boolean(
                        selectedConversation.isOnline ||
                          selectedConversation.session?.isOnline
                      )
                    )}
                  </p>
                  <p className="support-profile-activity__hint">
                    Indica si el widget de soporte está conectado en este momento.
                  </p>
                </div>
                <div className="support-profile-section">
                  <h4>Enrutamiento de conversaciones</h4>
                  <p>
                    {selectedConversation.consultationLabel || "Ninguno asignado"}
                  </p>
                </div>
                <div className="support-profile-section support-profile-section--company">
                  <h4>Empresa asociada en Wembii</h4>
                  <p>
                    <FontAwesomeIcon icon={faBuilding} />{" "}
                    <strong>{selectedConversation.wembiiCompanyName}</strong>
                  </p>
                  <p>
                    <FontAwesomeIcon icon={faUser} /> Contacto:{" "}
                    {selectedConversation.name}
                  </p>
                </div>
                <div className="support-profile-section">
                  <h4>Información principal</h4>
                  <p>
                    <FontAwesomeIcon icon={faMapMarkerAlt} />{" "}
                    {formatLocationLabel(
                      selectedConversation.city,
                      selectedConversation.country,
                      selectedConversation.session
                    )}
                  </p>
                  <p>
                    <FontAwesomeIcon icon={faClock} />{" "}
                    {formatLocalTime(selectedConversation.session?.timezone)} (
                    {selectedConversation.session?.timezone || "local"})
                  </p>
                  {selectedConversation.phoneNumber ? (
                    <p>
                      {getWhatsAppUrl(selectedConversation.phoneNumber) ? (
                        <a
                          className="support-profile__whatsapp"
                          href={getWhatsAppUrl(selectedConversation.phoneNumber)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <FontAwesomeIcon icon={faWhatsapp} />{" "}
                          {selectedConversation.phoneNumber}
                        </a>
                      ) : (
                        <a href={`tel:${selectedConversation.phoneNumber}`}>
                          <FontAwesomeIcon icon={faPhone} />{" "}
                          {selectedConversation.phoneNumber}
                        </a>
                      )}
                    </p>
                  ) : null}
                  <p>
                    <FontAwesomeIcon icon={faGlobeAmericas} /> {selectedConversation.flag}
                  </p>
                  <p>
                    <FontAwesomeIcon icon={faCommentDots} /> Chat
                  </p>
                </div>
                <div className="support-profile-section">
                  <h4>Redes e integraciones</h4>
                  {(selectedConversation.integrations || []).length ? (
                    <div className="support-integrations">
                      {selectedConversation.integrations!.map((integration) => (
                        <span
                          key={`${integration.provider}-${integration.label}`}
                          className="support-integration-chip"
                          title={integration.label}
                        >
                          <FontAwesomeIcon
                            icon={getIntegrationIcon(integration.provider)}
                          />
                          {integration.label}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p>Sin redes ni integraciones conectadas</p>
                  )}
                </div>
                <div className="support-profile-section">
                  <h4>Dispositivo del visitante</h4>
                  <p>
                    {selectedConversation.session?.browser || "Navegador desconocido"}{" "}
                    {selectedConversation.session?.os
                      ? `en ${selectedConversation.session.os}`
                      : ""}
                  </p>
                  <p>
                    {formatIpLabel(selectedConversation.session?.ipAddress)}
                    {selectedConversation.session?.country
                      ? ` · ${selectedConversation.session.country}`
                      : ""}
                  </p>
                </div>
                <div className="support-profile-section">
                  <h4>Participantes de la conversación</h4>
                  {(selectedConversation.participants || [
                    { email: selectedConversation.email, name: selectedConversation.name },
                  ]).map((participant) => (
                    <p key={participant.email}>
                      <FontAwesomeIcon icon={faEnvelope} /> {participant.email}
                    </p>
                  ))}
                </div>
                  </>
                ) : null}
              </aside>
            </div>
          ) : (
            <div className="support-appearance">
              <section className="support-appearance__panel">
                <header className="support-appearance__head">
                  <h3>Configuración del chat visible para usuarios</h3>
                  <button
                    type="button"
                    onClick={() => resetAppearance()}
                  >
                    Restablecer
                  </button>
                </header>

                <div className="support-appearance__sections">
                  <section className="support-appearance-card">
                    <h4>Tema del chatbox</h4>
                    <div className="support-appearance-row">
                      <span>Color del encabezado</span>
                      <input
                        type="color"
                        value={appearance.headerColor}
                        onChange={(e) =>
                          setAppearanceField("headerColor", e.target.value)
                        }
                      />
                    </div>
                    <div className="support-appearance-row">
                      <span>Color de burbuja</span>
                      <input
                        type="color"
                        value={appearance.bubbleColor}
                        onChange={(e) =>
                          setAppearanceField("bubbleColor", e.target.value)
                        }
                      />
                    </div>
                    <div className="support-appearance-row">
                      <span>Fondo del widget</span>
                      <input
                        type="color"
                        value={appearance.widgetBg}
                        onChange={(e) =>
                          setAppearanceField("widgetBg", e.target.value)
                        }
                      />
                    </div>
                    <div className="support-appearance-row">
                      <span>Color de texto</span>
                      <input
                        type="color"
                        value={appearance.textColor}
                        onChange={(e) =>
                          setAppearanceField("textColor", e.target.value)
                        }
                      />
                    </div>
                    <div className="support-appearance-row">
                      <span>Color de acento</span>
                      <input
                        type="color"
                        value={appearance.accentColor}
                        onChange={(e) =>
                          setAppearanceField("accentColor", e.target.value)
                        }
                      />
                    </div>
                    <div className="support-appearance-row">
                      <span>Bordes widget</span>
                      <select
                        value={appearance.roundedStyle}
                        onChange={(e) =>
                          setAppearanceField(
                            "roundedStyle",
                            e.target.value as SupportChatAppearance["roundedStyle"]
                          )
                        }
                      >
                        <option value="soft">Suave</option>
                        <option value="rounded">Redondeado</option>
                        <option value="square">Cuadrado</option>
                      </select>
                    </div>
                  </section>

                  <section className="support-appearance-card">
                    <h4>Textos del chatbox</h4>
                    <div className="support-appearance-row">
                      <span>Título del widget</span>
                      <input
                        value={appearance.widgetTitle}
                        onChange={(e) =>
                          setAppearanceField("widgetTitle", e.target.value)
                        }
                      />
                    </div>
                    <div className="support-appearance-row">
                      <span>Título de bienvenida</span>
                      <input
                        value={appearance.welcomeTitle}
                        onChange={(e) =>
                          setAppearanceField("welcomeTitle", e.target.value)
                        }
                      />
                    </div>
                    <div className="support-appearance-row support-appearance-row--textarea">
                      <span>Mensaje de bienvenida</span>
                      <textarea
                        rows={3}
                        value={appearance.welcomeMessage}
                        onChange={(e) =>
                          setAppearanceField("welcomeMessage", e.target.value)
                        }
                      />
                    </div>
                    <div className="support-appearance-row">
                      <span>Placeholder del input</span>
                      <input
                        value={appearance.inputPlaceholder}
                        onChange={(e) =>
                          setAppearanceField("inputPlaceholder", e.target.value)
                        }
                      />
                    </div>
                    <div className="support-appearance-row">
                      <span>CTA principal</span>
                      <input
                        value={appearance.ctaLabel}
                        onChange={(e) =>
                          setAppearanceField("ctaLabel", e.target.value)
                        }
                      />
                    </div>
                    <div className="support-appearance-row">
                      <span>Estado online</span>
                      <input
                        value={appearance.onlineLabel}
                        onChange={(e) =>
                          setAppearanceField("onlineLabel", e.target.value)
                        }
                      />
                    </div>
                  </section>

                  <section className="support-appearance-card">
                    <h4>Botón del chatbox</h4>
                    <div className="support-appearance-row">
                      <span>Posición del launcher</span>
                      <select
                        value={appearance.position}
                        onChange={(e) =>
                          setAppearanceField(
                            "position",
                            e.target.value as SupportChatAppearance["position"]
                          )
                        }
                      >
                        <option value="right">Derecha</option>
                        <option value="left">Izquierda</option>
                      </select>
                    </div>
                    <div className="support-appearance-row">
                      <span>Avatar principal</span>
                      <select
                        value={appearance.avatarType}
                        onChange={(e) =>
                          setAppearanceField(
                            "avatarType",
                            e.target.value as SupportChatAppearance["avatarType"]
                          )
                        }
                      >
                        <option value="logo">Logo Wembii</option>
                        <option value="agent">Agente</option>
                      </select>
                    </div>
                    {appearance.avatarType === "logo" ? (
                      <>
                        <div className="support-appearance-row">
                          <span>Logo personalizado</span>
                          <div className="support-appearance-avatar-upload">
                            {appearance.avatar ? (
                              <img
                                src={appearance.avatar}
                                alt=""
                                className="support-appearance-avatar-upload__preview"
                              />
                            ) : null}
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) uploadAvatar(file).catch(() => undefined);
                                e.target.value = "";
                              }}
                            />
                          </div>
                        </div>
                        <p className="support-appearance-card__hint">
                          Con &quot;Logo Wembii&quot; se muestra el logo de Wembii en el
                          chat. Puedes subir un logo de marca para reemplazarlo.
                        </p>
                      </>
                    ) : (
                      <p className="support-appearance-card__hint">
                        En modo Agente, el encabezado muestra hasta 4 admins en línea con
                        su foto de perfil. Si nadie está online, se muestra el equipo demo
                        con el logo de Wembii. El último en responder se destaca a la derecha.
                      </p>
                    )}
                    <label className="support-appearance-switch">
                      <span>Mostrar nombre de agente</span>
                      <input
                        type="checkbox"
                        checked={appearance.showAgentName}
                        onChange={(e) =>
                          setAppearanceField("showAgentName", e.target.checked)
                        }
                      />
                    </label>
                    <label className="support-appearance-switch">
                      <span>Mostrar timestamp en mensajes</span>
                      <input
                        type="checkbox"
                        checked={appearance.showTimestamp}
                        onChange={(e) =>
                          setAppearanceField("showTimestamp", e.target.checked)
                        }
                      />
                    </label>
                  </section>

                  <section className="support-appearance-card">
                    <h4>Botones rápidos del chat</h4>
                    <p className="support-appearance-card__hint">
                      Define si el chat abre con consulta comercial, técnica, ambas o ninguna.
                    </p>
                    <label className="support-appearance-switch">
                      <span>Mostrar clasificación inicial</span>
                      <input
                        type="checkbox"
                        checked={appearance.showQuickButtons}
                        onChange={(e) =>
                          setAppearanceField("showQuickButtons", e.target.checked)
                        }
                      />
                    </label>
                    <label className="support-appearance-switch">
                      <span>Mostrar botón comercial</span>
                      <input
                        type="checkbox"
                        checked={appearance.showCommercialQuickButton}
                        onChange={(e) =>
                          setAppearanceField(
                            "showCommercialQuickButton",
                            e.target.checked
                          )
                        }
                        disabled={!appearance.showQuickButtons}
                      />
                    </label>
                    <div className="support-appearance-row">
                      <span>Texto botón comercial</span>
                      <input
                        value={appearance.quickButtonCommercial}
                        onChange={(e) =>
                          setAppearanceField("quickButtonCommercial", e.target.value)
                        }
                        disabled={
                          !appearance.showQuickButtons ||
                          !appearance.showCommercialQuickButton
                        }
                      />
                    </div>
                    <label className="support-appearance-switch">
                      <span>Mostrar botón técnico</span>
                      <input
                        type="checkbox"
                        checked={appearance.showTechnicalQuickButton}
                        onChange={(e) =>
                          setAppearanceField(
                            "showTechnicalQuickButton",
                            e.target.checked
                          )
                        }
                        disabled={!appearance.showQuickButtons}
                      />
                    </label>
                    <div className="support-appearance-row">
                      <span>Texto botón técnico</span>
                      <input
                        value={appearance.quickButtonTechnical}
                        onChange={(e) =>
                          setAppearanceField("quickButtonTechnical", e.target.value)
                        }
                        disabled={
                          !appearance.showQuickButtons ||
                          !appearance.showTechnicalQuickButton
                        }
                      />
                    </div>
                  </section>
                </div>
              </section>

              <section className="support-appearance__preview-wrap">
                <header>
                  <h4>Preview en vivo</h4>
                  <span>Así verá el chat un usuario final de Wembii</span>
                </header>
                <SupportChatLivePreview appearance={appearance} />
              </section>
            </div>
          )}
        </div>
      </Content>
    </Layout>
  );
}
